import { useLocation } from 'react-router';
import {
  Button,
  Loading,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'snap-design-system';
import { Check, Power } from 'lucide-react';
import { REPORT_DETAIL_STORE_INSTANCE } from '~/store/details';
import { useAutoSaveToggle } from '~/hooks/useAutoSaveToggle';
import { toast } from 'sonner';

type ButtonVariant = 'primary' | 'filled' | 'ghost' | 'accent' | 'inverted';

interface StatusConfig {
  text: string;
  icon: React.ReactNode;
  variant: ButtonVariant;
  className?: string;
}

const AutoSaveStatus = () => {
  const location = useLocation();
  const isReportDetailsPage = location.pathname.startsWith('/report/');
  const { selectors } = REPORT_DETAIL_STORE_INSTANCE;
  const isPending = selectors.useHasPendingChanges();
  const isEnabled = selectors.useIsAutoSaveEnabled();

  const { enableAutoSave, disableAutoSave } = useAutoSaveToggle();

  if (!isReportDetailsPage) {
    return null;
  }

  const handleToggle = () => {
    if (isPending) return;

    if (isEnabled) {
      disableAutoSave();
      toast.warning('Autosave desativado.', {
        description: 'Necessário salvar as alterações manualmente.',
      });
    } else {
      enableAutoSave();
      toast.info('Autosave ativado.', {
        description: 'Alterações serão salvas automaticamente.',
      });
    }
  };

  const getStatusConfig = (): StatusConfig => {
    if (!isEnabled) {
      return {
        text: 'Autosave desativado',
        icon: <Power size={16} />,
        variant: 'ghost',
        className: 'opacity-60',
      };
    }

    if (isPending) {
      return {
        text: 'Salvando alterações...',
        icon: <Loading size="sm" color='white'/>,
        variant: 'accent',
        className: 'disabled:opacity-100',
      };
    }

    return {
      text: 'Autosave ativo',
      icon: <Check size={16} />,
      variant: 'primary',
    };
  };

  const { text, icon, variant, className } = getStatusConfig();

  const tooltipContent = isPending
    ? 'Aguarde o salvamento concluir'
    : isEnabled
      ? 'Clique para desativar autosave. Sua preferência será mantida durante a sessão.'
      : 'Clique para ativar autosave. Sua preferência será mantida durante a sessão.';

  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <Button
          variant={variant}
          size="small"
          className={`w-[288px] transition-all duration-300 ${className || ''}`}
          onClick={handleToggle}
          disabled={isPending}
          icon={icon}
          iconPosition="right"
        >
          {text}
        </Button>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        variant="chamfered"
        chamfer="topLeft"
      >
        <div className="px-4 py-2">
          <Text variant={"body-sm"} color='subtle' className="text-left">
            {tooltipContent}
          </Text>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

export default AutoSaveStatus;
